import type { IFunnelSelectionItem } from '~/presentation/charts/components/GFunnelChart/components/FunnelSelection/type';
import type { IOnChangeFunnelParams } from '~/presentation/charts/components/GFunnelChart/components/types';
import type { GVizFunnelChartProps, GDataPoint } from '~/presentation/charts';
import type { IBasePageAnalyticMetric } from '~/modules/page-analytic/types';
import type { IBasePathAnalyticPageWithChild } from '../types';

import {
  getTotalSessionCompletedCheckoutMetric,
  getTotalSessionReachedCheckoutMetric,
  getTotalSessionWithCartAdditionMetric,
  numberWithCommas,
  PA_METRIC_TOOLTIP,
} from '~/modules/analytics';
import { calculateConversionFunnelTrends, getFunnelChartTooltip } from '../helpers';
import { METRICS_COLUMNS, SHOPIFY_PAGE_TYPE_KEY } from '~/modules/page-analytic';
import { usePathAnalysisHeatmap } from './usePathAnalysisHeatmap';
import { usePathAnalysisContext } from '~/modules/path-analysis';
import { useConvertMoneyByCurrency } from '~/modules/shopify';
import { IPAMetricKey } from '~/modules/analytics/types';
import { MAX_FUNNEL_ADDITIONAL_LEVEL } from '../configs';
import { useFeatureFlagChecker } from '~/modules/apps';
import { DeleteIcon } from '@shopify/polaris-icons';
import { useSearchParams } from '@remix-run/react';
import { TemplateType } from '~/types/template';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { parseTemplateType } from '~/utils';

const FUNNEL_NAME = 'Conversion';
// Checkout pages don't support heatmap viewing
const NON_HEATMAP_PAGE_TYPES = new Set<string>([TemplateType.CHECKOUT]);

export const usePathAnalysisChart = () => {
  const [searchParams] = useSearchParams();

  const { t } = useTranslation();
  const { getTextPrice } = useConvertMoneyByCurrency();
  const { handleViewHeatmap } = usePathAnalysisHeatmap();

  const shopifyPageType = searchParams.get(SHOPIFY_PAGE_TYPE_KEY);

  const {
    pageWithMetric,
    nextLevelPages,
    dynamicLevels,
    previousPathAnalyticsMap,
    handleAddNextLevel,
    handleChangeLevel,
    getPagesByParentID,
    handleDeleteLevel,
    isPriceCampaign,
  } = usePathAnalysisContext();

  const orderedPages = useMemo(
    () => [pageWithMetric, ...dynamicLevels].filter(Boolean),
    [pageWithMetric, dynamicLevels],
  );

  const { isEnabledHeatmapV1, isEnabledHeatmapV2 } = useFeatureFlagChecker();

  const chartTooltipPrimaryAction = useMemo(() => {
    if (!isEnabledHeatmapV1) return undefined;

    return {
      content: t('View heatmap'),
      onClick: (item?: GDataPoint) => {
        if (!item?.id) return;

        const level = orderedPages.findIndex((page) => page?.id === item.id);
        if (level === -1) return;

        handleViewHeatmap(level, orderedPages as IBasePathAnalyticPageWithChild[]);
      },
    };
  }, [handleViewHeatmap, orderedPages, t, isEnabledHeatmapV1]);

  const lengthDisplayedLevels = dynamicLevels.length;

  const isDisabledAddICon =
    !nextLevelPages.length || lengthDisplayedLevels >= MAX_FUNNEL_ADDITIONAL_LEVEL;

  const lastLevel = useMemo(() => {
    return dynamicLevels[dynamicLevels.length - 1] || pageWithMetric;
  }, [dynamicLevels, pageWithMetric]);

  const getTooltipAction = useCallback(
    (pageType?: string) => {
      if (pageType && NON_HEATMAP_PAGE_TYPES.has(pageType)) return undefined;
      return chartTooltipPrimaryAction;
    },
    [chartTooltipPrimaryAction],
  );

  const onDeleteFunnel = useCallback(
    (id?: string) => {
      handleDeleteLevel(id);
    },
    [handleDeleteLevel],
  );

  const getConversionFunnelData = useCallback(
    (item?: IBasePathAnalyticPageWithChild) => {
      const metric = item?.metric as IBasePageAnalyticMetric;

      const totalAddedToCart = getTotalSessionWithCartAdditionMetric({ metric }).value;
      const totalReachedCheckout = getTotalSessionReachedCheckoutMetric({ metric }).value;
      const totalCompletedCheckout = getTotalSessionCompletedCheckoutMetric({ metric }).value;

      return [
        {
          value: Number(totalAddedToCart ?? 0),
          key: METRICS_COLUMNS[IPAMetricKey.PAGE_ADDED_TO_CART].label,
          labelTooltip: PA_METRIC_TOOLTIP[IPAMetricKey.PAGE_CART_ADDITION],
        },
        {
          value: Number(totalReachedCheckout ?? 0),
          key: t('Reached checkout'),
          labelTooltip: PA_METRIC_TOOLTIP[IPAMetricKey.PAGE_REACHED_CHECKOUT],
        },
        {
          value: Number(totalCompletedCheckout ?? 0),
          key: t('Completed checkout'),
          labelTooltip: PA_METRIC_TOOLTIP[IPAMetricKey.PAGE_COMPLETE_CHECKOUT],
        },
      ];
    },
    [t],
  );

  const listFunnelDataMiddle = useMemo(
    () =>
      dynamicLevels.reduce(
        (acc, item) => {
          const key =
            parseTemplateType(item?.pageType as TemplateType) + `-${item.id}` || 'unknown';

          // Current data
          acc.currentData.push({
            value: item.metric?.totalSessions || 0,
            key,
            parentID: item.parentID,
            id: item.id,
            chartTooltip: getFunnelChartTooltip({
              page: item,
              getTextPrice,
            }),
            chartTooltipPrimaryAction: isEnabledHeatmapV2
              ? getTooltipAction(item?.pageType)
              : undefined,
            secondaryActions: [
              {
                onAction: () => onDeleteFunnel(item.id),
                icon: DeleteIcon,
              },
            ],
            title: item.pageTitle,
          });

          // Previous data
          acc.previousData.push({
            value: previousPathAnalyticsMap[item.id]?.metric?.totalSessions || 0,
            key,
            title: item.pageTitle,
          });

          return acc;
        },
        { currentData: [] as GDataPoint[], previousData: [] as GDataPoint[] },
      ),
    [
      dynamicLevels,
      previousPathAnalyticsMap,
      onDeleteFunnel,
      getTextPrice,
      getTooltipAction,
      isEnabledHeatmapV2,
    ],
  );
  const lastLevelPreviousData = previousPathAnalyticsMap[lastLevel?.id];
  const funnelData = useMemo<GVizFunnelChartProps['data']>(() => {
    const firstLevelKey =
      pageWithMetric?.pageTitle ||
      parseTemplateType((pageWithMetric?.pageType as TemplateType) ?? shopifyPageType) ||
      'unknown';
    return [
      {
        data: [
          {
            value: pageWithMetric?.metric?.totalSessions || 0,
            key: firstLevelKey,
            title: pageWithMetric?.pageTitle,
            id: pageWithMetric?.id,
            chartTooltip: getFunnelChartTooltip({ page: pageWithMetric, getTextPrice }),
            chartTooltipPrimaryAction: getTooltipAction(pageWithMetric?.pageType),
          },
          ...listFunnelDataMiddle.currentData,
          ...getConversionFunnelData(lastLevel),
        ],
        name: FUNNEL_NAME,
        metadata: {
          trends: calculateConversionFunnelTrends({
            pageWithMetric,
            previousPathAnalyticsMap,
            dynamicLevels,
          }),
        },
      },
      {
        name: FUNNEL_NAME,
        data: [
          {
            value: previousPathAnalyticsMap[pageWithMetric?.id || '']?.metric?.totalSessions || 0,
            key: firstLevelKey,
          },
          ...listFunnelDataMiddle.previousData,
          ...getConversionFunnelData(lastLevelPreviousData),
        ],
        isComparison: true,
        isEmptyData: !lastLevelPreviousData || lastLevelPreviousData.metric?.totalSessions === 0,
      },
    ];
  }, [
    dynamicLevels,
    pageWithMetric,
    shopifyPageType,
    getTextPrice,
    previousPathAnalyticsMap,
    listFunnelDataMiddle,
    getConversionFunnelData,
    lastLevel,
    lastLevelPreviousData,
    getTooltipAction,
  ]);

  const formatFunnelSelection = (item: IBasePathAnalyticPageWithChild): IFunnelSelectionItem => {
    const childrenSessions = item.metric?.totalSessions || 0;

    return {
      id: item.id,
      title: item.pageTitle || parseTemplateType(item.pageType as TemplateType),
      path: item.locationPath,
      percentage: numberWithCommas(childrenSessions.toString()),
      parentID: item.parentID,
    };
  };

  const sortByPercentage = (a: IFunnelSelectionItem, b: IFunnelSelectionItem) => {
    const numericPattern = /[^0-9.-]/g;
    const aPercentage = Number(a.percentage.replace(numericPattern, ''));
    const bPercentage = Number(b.percentage.replace(numericPattern, ''));

    return bPercentage - aPercentage;
  };

  // const sortByTitle = (a: IFunnelSelectionItem, b: IFunnelSelectionItem) => {
  //   const aTitle = a.title || '';
  //   const bTitle = b.title || '';
  //   return aTitle.localeCompare(bTitle);
  // };

  //list funnel selection to add new funnel
  const listFunnelSelection = useMemo((): IFunnelSelectionItem[] => {
    if (!nextLevelPages) return [];
    return nextLevelPages.map(formatFunnelSelection).sort(sortByPercentage);
  }, [nextLevelPages]);

  //list funnel selection to change funnel
  const getListFunnelByID = (data: {
    level?: number;
    parentID?: string;
  }): IFunnelSelectionItem[] => {
    const { level, parentID } = data;

    const availableChildPages = getPagesByParentID({ level, parentID });
    // if (level === 0) {
    //   return availableChildPages.map(formatFunnelSelection).sort(sortByTitle);
    // }

    return availableChildPages.map(formatFunnelSelection).sort(sortByPercentage);
  };

  const onAddNextFunnel = (id: string) => {
    handleAddNextLevel(id);
  };

  const onChangeFunnel = (data: IOnChangeFunnelParams) => {
    if (!data.selectedID) return;
    handleChangeLevel(data);
  };

  return {
    lengthDisplayedLevels,
    listFunnelSelection,
    isDisabledAddICon,
    hideAddICon: isPriceCampaign,
    funnelData,
    onDeleteFunnel,
    onAddNextFunnel,
    onChangeFunnel,
    getListFunnelByID,
  };
};
